import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { userPromptsApi, type CreateUserPromptInput, type UserPrompt } from '../api/userPrompts.api';

export type { CreateUserPromptInput, UserPrompt };

export const USER_PROMPTS_QUERY_KEY = [
  'user-prompts',
  'hector_agent_casediscovery',
] as const;

const EMPTY_USER_PROMPTS: UserPrompt[] = [];

export interface UseUserPromptsReturn {
  prompts: UserPrompt[];
  isLoading: boolean;
  error: string | null;
  createPrompt: (data: CreateUserPromptInput) => Promise<UserPrompt>;
  updatePrompt: (id: string, data: { title?: string; description?: string; prompt_content?: string }) => Promise<UserPrompt>;
  setPromptHidden: (id: string, hidden: boolean) => Promise<UserPrompt>;
  deletePrompt: (id: string) => Promise<void>;
  reorderPrompts: (items: { id: string; sort_order: number }[]) => Promise<void>;
}

export function useUserPrompts(): UseUserPromptsReturn {
  const queryClient = useQueryClient();
  const promptsQuery = useQuery({
    queryKey: USER_PROMPTS_QUERY_KEY,
    queryFn: userPromptsApi.list,
  });

  const createPrompt = useCallback(async (
    data: CreateUserPromptInput,
  ): Promise<UserPrompt> => {
    const created = await userPromptsApi.create(data);
    // WHY: the prompt library can be rendered by several chat controls at once.
    // Updating their shared server-state cache keeps every consumer in sync while
    // preserving the existing immediate post-mutation UI update.
    queryClient.setQueryData<UserPrompt[]>(USER_PROMPTS_QUERY_KEY, (current) =>
      [...(current ?? []), created].sort((a, b) => a.sort_order - b.sort_order),
    );
    return created;
  }, [queryClient]);

  const updatePrompt = useCallback(async (
    id: string,
    data: { title?: string; description?: string; prompt_content?: string },
  ): Promise<UserPrompt> => {
    const updated = await userPromptsApi.update(id, data);
    queryClient.setQueryData<UserPrompt[]>(USER_PROMPTS_QUERY_KEY, (current) =>
      (current ?? []).map((prompt) => (prompt.id === id ? updated : prompt)),
    );
    return updated;
  }, [queryClient]);

  const setPromptHidden = useCallback(async (id: string, hidden: boolean): Promise<UserPrompt> => {
    const updated = await userPromptsApi.setHidden(id, hidden);
    queryClient.setQueryData<UserPrompt[]>(USER_PROMPTS_QUERY_KEY, (current) =>
      (current ?? []).map((prompt) => (prompt.id === id ? updated : prompt)),
    );
    return updated;
  }, [queryClient]);

  const deletePrompt = useCallback(async (id: string): Promise<void> => {
    await userPromptsApi.delete(id);
    queryClient.setQueryData<UserPrompt[]>(USER_PROMPTS_QUERY_KEY, (current) =>
      (current ?? []).filter((prompt) => prompt.id !== id),
    );
  }, [queryClient]);

  const reorderPrompts = useCallback(async (
    items: { id: string; sort_order: number }[],
  ): Promise<void> => {
    try {
      const reordered = await userPromptsApi.reorder(items);
      queryClient.setQueryData<UserPrompt[]>(USER_PROMPTS_QUERY_KEY, reordered);
    } catch (error) {
      // Un refus (409 : permutation incomplète) signale un cache périmé — une
      // action créée par l'agent, un autre onglet, des natives amorcées depuis.
      // Relire la bibliothèque rétablit l'ordre réel à l'écran.
      await queryClient.invalidateQueries({ queryKey: ['user-prompts'] });
      throw error;
    }
  }, [queryClient]);

  return {
    prompts: promptsQuery.data ?? EMPTY_USER_PROMPTS,
    isLoading: promptsQuery.isLoading,
    error: promptsQuery.isError
      ? 'Erreur lors du chargement des actions rapides'
      : null,
    createPrompt,
    updatePrompt,
    setPromptHidden,
    deletePrompt,
    reorderPrompts,
  };
}
